import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { createClientAccount, getClientAccount, resetClientAccountPassword } from '../api';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<any>(null);
  const [payment, setPayment] = useState({ Amount: '', Date: '' });
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({ name: '', venueId: null as number | null, eventDate: '', contractPrice: '', notes: '', status: 'Prospective', statusNotes: '' });
  const [venues, setVenues] = useState<any[]>([]);
  const [venueSearch, setVenueSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [clientAccount, setClientAccount] = useState<{ id: number; username: string; email: string } | null>(null);
  const [clientAccountUsername, setClientAccountUsername] = useState('');
  const [clientAccountPassword, setClientAccountPassword] = useState('');
  const [clientAccountEmail, setClientAccountEmail] = useState('');
  const [clientAccountLink, setClientAccountLink] = useState('');
  const [clientAccountError, setClientAccountError] = useState('');
  const [clientAccountSuccess, setClientAccountSuccess] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchClient();
    fetchVenues();
    fetchClientAccount();
  }, [id]);

  const fetchClient = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientData = response.data;
      if (clientData.submittedPayments && typeof clientData.submittedPayments === 'string') {
        try {
          clientData.submittedPayments = JSON.parse(clientData.submittedPayments);
        } catch (parseError) {
          console.error('Failed to parse submittedPayments:', parseError);
          clientData.submittedPayments = [];
        }
      } else {
        clientData.submittedPayments = [];
      }
      clientData.contracts = clientData.contracts || [];
      setClient(clientData);
      setEditData({
        name: clientData.name,
        venueId: clientData.venueId || null,
        eventDate: new Date(clientData.eventDate).toISOString().split('T')[0],
        contractPrice: clientData.contractPrice.toString(),
        notes: clientData.notes || '',
        status: clientData.status || 'Prospective',
        statusNotes: clientData.statusNotes || ''
      });
    } catch (error: any) {
      console.error('Failed to fetch client:', error);
      setError('Failed to load client. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Venues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVenues(response.data);
    } catch (error: any) {
      console.error('Failed to fetch venues:', error);
      setError('Failed to load venues.');
    }
  };

  const fetchClientAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await getClientAccount(id!, token);
      setClientAccount(response || null);
    } catch (error: any) {
      console.error('Failed to fetch client account:', error);
      setClientAccount(null);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payment.Amount || !payment.Date) {
      setError('Amount and Date are required for payments.');
      return;
    }
    if (parseFloat(payment.Amount) <= 0) {
      setError('Amount must be greater than 0.');
      return;
    }
    if (!window.confirm(`Are you sure you want to add a payment of $${parseFloat(payment.Amount).toFixed(2)} on ${new Date(payment.Date).toLocaleDateString()}?`)) {
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/Clients/${id}/payment`,
        {
          Amount: parseFloat(payment.Amount),
          Date: new Date(payment.Date)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayment({ Amount: '', Date: '' });
      setSuccess('Payment added successfully!');
      fetchClient();
    } catch (error: any) {
      console.error('Failed to add payment:', error);
      setError('Failed to add payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePayment = async (index: number) => {
    const paymentToDelete = client.submittedPayments[index];
    if (!window.confirm(`Are you sure you want to delete the payment of $${paymentToDelete.Amount.toFixed(2)} on ${new Date(paymentToDelete.Date).toLocaleDateString()}?`)) {
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/Clients/${id}/payment/${index}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Payment deleted successfully!');
      fetchClient();
    } catch (error: any) {
      console.error('Failed to delete payment:', error);
      setError('Failed to delete payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editData.name || !editData.eventDate || !editData.contractPrice) {
      setError('Name, Event Date, and Contract Price are required.');
      return;
    }
    if (parseFloat(editData.contractPrice) <= 0) {
      setError('Contract Price must be greater than 0.');
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Clients/${id}`,
        {
          name: editData.name,
          venueId: editData.venueId,
          eventDate: new Date(editData.eventDate),
          contractPrice: parseFloat(editData.contractPrice),
          notes: editData.notes,
          submittedPayments: client.submittedPayments,
          status: editData.status,
          statusNotes: editData.statusNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowModal(false);
      setSuccess('Client updated successfully!');
      fetchClient();
    } catch (error: any) {
      console.error('Failed to update client:', error);
      setError('Failed to update client. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string, newStatusNotes: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Clients/${id}`,
        {
          ...client,
          status: newStatus,
          statusNotes: newStatusNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditData({ ...editData, status: newStatus, statusNotes: newStatusNotes });
      setSuccess('Status updated successfully!');
      fetchClient();
    } catch (error: any) {
      console.error('Failed to update status:', error);
      setError('Failed to update status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClientAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setClientAccountError('');
    setClientAccountSuccess('');
    setClientAccountLink('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');

      await createClientAccount(id!, clientAccountUsername, clientAccountPassword, clientAccountEmail, token);

      const loginLink = `${process.env.REACT_APP_FRONTEND_BASE_URL}/client/login`;
      setClientAccountLink(loginLink);
      setClientAccountSuccess('Client account created successfully!');
      fetchClientAccount();
    } catch (error: any) {
      console.error('Failed to create client account:', error);
      if (error.response?.status === 401) {
        setClientAccountError('Unauthorized: Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setClientAccountError('Failed to create client account. Username might already exist.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setClientAccountError('');
    setClientAccountSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');

      await resetClientAccountPassword(id!, newPassword, token);
      setClientAccountSuccess('Password reset successfully. Client must reset their password on next login.');
      setNewPassword('');
    } catch (error: any) {
      console.error('Failed to reset password:', error);
      setClientAccountError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!client) return <div className="container mt-5">{isLoading ? 'Loading...' : error || 'Loading...'}</div>;

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{client.name}</h2>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/clients')}
          disabled={isLoading}
        >
          Back to Clients
        </button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {isLoading && <div className="alert alert-info">Loading...</div>}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Client Details</h4>
        </div>
        <div className="card-body">
          <p>
            <strong>Venue:</strong>{' '}
            {client.venueName ? (
              <Link to={`/venues/${client.venueId}`} className="text-primary fw-bold">
                {client.venueName}
              </Link>
            ) : (
              'N/A'
            )}
          </p>
          <p><strong>Event Date:</strong> {new Date(client.eventDate).toLocaleDateString()}</p>
          <p><strong>Contract Price:</strong> ${client.contractPrice.toFixed(2)}</p>
          <p><strong>Current Balance:</strong> ${client.currentBalance.toFixed(2)}</p>
          <p>
            <strong>Status:</strong>{' '}
            <select
              className="form-select d-inline-block w-auto mb-1"
              value={editData.status}
              onChange={(e) => handleStatusChange(e.target.value, editData.statusNotes)}
              disabled={isLoading}
            >
              <option value="Prospective">Prospective</option>
              <option value="Contract Sent">Contract Sent</option>
              <option value="Booked">Booked</option>
            </select>
            <input
              type="text"
              className="form-control d-inline-block w-auto"
              placeholder="Status notes"
              value={editData.statusNotes}
              onChange={(e) => handleStatusChange(editData.status, e.target.value)}
              disabled={isLoading}
            />
          </p>
          <p><strong>Notes:</strong> {client.notes || 'N/A'}</p>
          <button
            className="btn btn-outline-primary mb-3"
            onClick={() => setShowModal(true)}
            disabled={isLoading}
          >
            Edit Client
          </button>
          <h5>Payments</h5>
          {client.submittedPayments.length === 0 ? (
            <p className="text-muted">No payments recorded.</p>
          ) : (
            <ul className="list-group list-group-flush mb-3">
              {client.submittedPayments.map((p: any, i: number) => (
                <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>
                    ${p.Amount.toFixed(2)} on {new Date(p.Date).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleDeletePayment(i)}
                    className="btn btn-sm btn-danger"
                    disabled={isLoading}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={handleAddPayment} className="mb-3">
            <div className="row">
              <div className="col-md-3">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Amount"
                  value={payment.Amount}
                  onChange={(e) => setPayment({ ...payment, Amount: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="col-md-3">
                <input
                  type="date"
                  className="form-control"
                  value={payment.Date}
                  onChange={(e) => setPayment({ ...payment, Date: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="col-md-2">
                <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                  {isLoading ? 'Adding...' : 'Add Payment'}
                </button>
              </div>
            </div>
          </form>
          <h5>Forms</h5>
          {client.forms.length === 0 ? (
            <p className="text-muted">No forms associated with this client.</p>
          ) : (
            <ul className="list-group list-group-flush mb-3">
              {client.forms.map((form: any) => (
                <li key={form.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    {form.name || `Form ID: ${form.id}`}
                    <br />
                    <small>
                      Status: {form.status} | Last Updated: {new Date(form.lastUpdated).toLocaleString()}
                    </small>
                  </div>
                  <div>
                    <Link to={`/form-details/${form.id}`} className="btn btn-outline-primary btn-sm">
                      View Details
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <h5>Contracts</h5>
          {client.contracts.length === 0 ? (
            <p className="text-muted">No contracts associated with this client.</p>
          ) : (
            <ul className="list-group list-group-flush">
              {client.contracts.map((contract: any) => (
                <li key={contract.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    {contract.name || `Contract ID: ${contract.id}`}
                    <br />
                    <small>
                      Status: {contract.status} | Last Updated: {new Date(contract.lastUpdated).toLocaleString()}
                    </small>
                  </div>
                  <div>
                    <Link to={`/contract-details/${contract.id}`} className="btn btn-outline-primary btn-sm">
                      View Details
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Client Account</h4>
        </div>
        <div className="card-body">
          {clientAccount ? (
            <>
              <p><strong>Username:</strong> {clientAccount.username}</p>
              <p><strong>Email:</strong> {clientAccount.email}</p>
              <h5>Reset Password</h5>
              <form onSubmit={handleResetPassword}>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {clientAccountError && <div className="alert alert-danger">{clientAccountError}</div>}
                {clientAccountSuccess && <div className="alert alert-success">{clientAccountSuccess}</div>}
                <div className="d-flex justify-content-center">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p className="text-muted mb-3">
                Create an account for this client to access their forms and contracts. After creating, share the login link with them.
              </p>
              <form onSubmit={handleCreateClientAccount}>
                <div className="mb-3">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter username for client"
                    value={clientAccountUsername}
                    onChange={(e) => setClientAccountUsername(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter client email"
                    value={clientAccountEmail}
                    onChange={(e) => setClientAccountEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter password for client"
                    value={clientAccountPassword}
                    onChange={(e) => setClientAccountPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {clientAccountError && <div className="alert alert-danger">{clientAccountError}</div>}
                {clientAccountSuccess && (
                  <div className="alert alert-success">
                    {clientAccountSuccess}
                    <br />
                    <a href={clientAccountLink} target="_blank" rel="noopener noreferrer">
                      {clientAccountLink}
                    </a>
                  </div>
                )}
                <div className="d-flex justify-content-center">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? 'show' : ''}`}
        style={{ display: showModal ? 'block' : 'none' }}
        id="editClientModal"
        tabIndex={-1}
        aria-labelledby="editClientModalLabel"
        aria-hidden={!showModal}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editClientModalLabel">Edit Client</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateClient}>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Venue</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search venues..."
                    value={venueSearch}
                    onChange={(e) => setVenueSearch(e.target.value)}
                    disabled={isLoading}
                  />
                  <select
                    className="form-control mt-2"
                    value={editData.venueId || ''}
                    onChange={(e) => setEditData({ ...editData, venueId: e.target.value ? parseInt(e.target.value) : null })}
                    disabled={isLoading}
                  >
                    <option value="">Select a venue (optional)</option>
                    {venues.filter(v => v.name.toLowerCase().includes(venueSearch.toLowerCase())).map(venue => (
                      <option key={venue.id} value={venue.id}>{venue.name} - {venue.address}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Event Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={editData.eventDate}
                    onChange={(e) => setEditData({ ...editData, eventDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Contract Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editData.contractPrice}
                    onChange={(e) => setEditData({ ...editData, contractPrice: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                    disabled={isLoading}
                  >
                    <option value="Prospective">Prospective</option>
                    <option value="Contract Sent">Contract Sent</option>
                    <option value="Booked">Booked</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Status Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.statusNotes}
                    onChange={(e) => setEditData({ ...editData, statusNotes: e.target.value })}
                    disabled={isLoading}
                    placeholder="Enter status notes (optional)"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    disabled={isLoading}
                    rows={3}
                    placeholder="Enter notes (optional)"
                  />
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setShowModal(false)}
        ></div>
      )}
    </div>
  );
};

export default ClientDetail;